import { httpsCallable } from 'firebase/functions';
import type { DeckIntelligence } from '../types';
import { functions } from './firebase';

const ANALYZE_CALLABLE_TIMEOUT_MS = 150_000;

export type AnalyzeBusinessOpportunityResponse = {
  ideaIntelligence: DeckIntelligence;
};

export function analyzeBusinessOpportunityErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: string }).message);
    if (message && !message.startsWith('Firebase')) {
      return message;
    }
  }
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: string }).code);
    if (code === 'functions/invalid-argument') {
      return 'Add at least 100 characters describing your idea before analyzing.';
    }
    if (code === 'functions/not-found') {
      return 'Business opportunity not found.';
    }
    if (code === 'functions/permission-denied') {
      return 'You do not have permission to analyze this opportunity.';
    }
    if (code === 'functions/unauthenticated') {
      return 'Sign in to analyze the idea.';
    }
    if (code === 'functions/unavailable') {
      return 'Analysis service is temporarily unavailable. Try again.';
    }
    if (code === 'functions/deadline-exceeded') {
      return 'Analysis timed out. Try again in a moment.';
    }
    if (code === 'functions/internal') {
      return 'Analysis failed. Try again.';
    }
  }
  return err instanceof Error ? err.message : 'Idea analysis failed.';
}

export async function analyzeBusinessOpportunity(opportunityId: string): Promise<DeckIntelligence> {
  const fn = httpsCallable<{ opportunityId: string }, AnalyzeBusinessOpportunityResponse>(
    functions,
    'analyzeBusinessOpportunity',
    { timeout: ANALYZE_CALLABLE_TIMEOUT_MS }
  );
  const result = await fn({ opportunityId: opportunityId.trim() });
  return result.data.ideaIntelligence;
}
